import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { Faq } from "@/components/Faq";
import { Button } from "@/components/ui/button";
import { breadcrumbSchema, faqSchema, jsonLd, SITE_URL, absUrl } from "@/lib/seo";
import { GENERIC_BOKADIREKT } from "@/lib/booking";

const BOOK_URL = GENERIC_BOKADIREKT;

const services = [
  { name: "Herrklippning", price: "450 kr" },
  { name: "Taper Fade / Skin Fade", price: "450 kr" },
  { name: "Skäggtrimning", price: "380 kr" },
  { name: "Herrklippning + Skäggtrimning", price: "680 kr" },
  { name: "Barnklippning", price: "380 kr" },
  { name: "Seniorklippning (mån–tors 10–14)", price: "270 kr" },
  { name: "Slätrakning", price: "390 kr" },
];

const landmarks = [
  { name: "T-Centralen", time: "1 min" },
  { name: "Sergels Torg", time: "3 min" },
  { name: "Drottninggatan", time: "2 min" },
  { name: "Stockholm Centralstation", time: "4 min" },
  { name: "Kulturhuset", time: "3 min" },
];

const faq = [
  { q: "Var finns barberaren i Norrmalm?", a: "Tailor's Barber Norrmalm ligger på plan 3 i Åhléns City, Klarabergsgatan 50 – en minut från T-Centralen." },
  { q: "Har ni öppet på helger i Norrmalm?", a: "Ja, lördag 10–19 och söndag 11–19." },
  { q: "Kan jag komma på drop-in i Norrmalm?", a: "Ja, drop-in är välkommet i mån av tid. Onlinebokning rekommenderas under helger." },
  { q: "Vad kostar klippning hos barberaren i Norrmalm?", a: "Herrklippning 450 kr, taper fade 450 kr, skäggtrimning 380 kr." },
];

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "BarberShop",
  name: "Tailor's Barber – Norrmalm (Åhléns City)",
  url: `${SITE_URL}/barberare-norrmalm`,
  telephone: "+46 8 000 000",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Klarabergsgatan 50, plan 3",
    postalCode: "111 21",
    addressLocality: "Stockholm",
    addressRegion: "Norrmalm",
    addressCountry: "SE",
  },
  areaServed: { "@type": "City", name: "Stockholm" },
  openingHoursSpecification: [
    { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "10:00", closes: "20:00" },
    { "@type": "OpeningHoursSpecification", dayOfWeek: "Saturday", opens: "10:00", closes: "19:00" },
    { "@type": "OpeningHoursSpecification", dayOfWeek: "Sunday", opens: "11:00", closes: "19:00" },
  ],
};

export const Route = createFileRoute("/barberare-norrmalm")({
  head: () => ({
    meta: [
      { title: "Barber Norrmalm: drop-in & bokning | Tailor's Barber" },
      { name: "description", content: "Barber Norrmalm i Stockholm City, 1 min från T-Centralen. Herrklippning 450 kr, fade och skäggtrimning. Drop-in och onlinebokning." },
      { property: "og:title", content: "Barber Norrmalm: drop-in & bokning | Tailor's Barber" },
      { property: "og:description", content: "Barber Norrmalm i Stockholm City. 1 min från T-Centralen. Herrklippning, fade och skäggtrimning." },
      { property: "og:url", content: absUrl("/barberare-norrmalm") },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: absUrl("/barberare-norrmalm") }],
    scripts: [
      jsonLd(localBusinessSchema),
      jsonLd(faqSchema(faq)),
      jsonLd(breadcrumbSchema([{ name: "Hem", url: "/" }, { name: "Barberare Norrmalm", url: "/barberare-norrmalm" }])),
    ],
  }),
  component: BarberareNorrmalmPage,
});

function BarberareNorrmalmPage() {
  return (
    <SiteShell>
      <section className="container-page pt-10 md:pt-16 pb-4">
        <nav className="text-xs text-muted-foreground mb-6">
          <Link to="/" className="hover:underline">Hem</Link>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">Barberare Norrmalm</span>
        </nav>
        <div className="eyebrow">Barberare Norrmalm</div>
        <h1 className="mt-3 text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
          Barber Norrmalm: Tailor&apos;s Barber vid T-Centralen
        </h1>
        <p className="mt-5 text-lg text-foreground/80 max-w-prose leading-relaxed">
          Letar du efter Barber Norrmalm? Tailor&apos;s Barber finns mitt i Norrmalm, Stockholms puls.
          Vår salong inne i Åhléns City på Klarabergsgatan ligger bokstavligen 60 sekunder från
          T-Centralen och är omgiven av Sergels Torg, Drottninggatan och Kulturhuset.
        </p>
        <div className="mt-6">
          <Button asChild size="lg">
            <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">Boka tid i Norrmalm</a>
          </Button>
        </div>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Var ligger vi i Norrmalm?</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          <strong>Adress:</strong>{" "}
          <a
            href="https://maps.google.com/?q=Klarabergsgatan+50+Stockholm"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Klarabergsgatan 50, plan 3 (herreavdelningen), Åhléns City, 111 21 Stockholm
          </a>
        </p>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Norrmalm är stadsdelen som rymmer T-Centralen, Sergels Torg och Åhléns. Vår salong
          befinner sig i hjärtat av detta område och är tillgänglig med tunnelbana (alla linjer),
          pendeltåg och buss.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Tjänster hos barberaren i Norrmalm</h2>
        <ul className="mt-6 divide-y divide-border/60 max-w-2xl">
          {services.map((s) => (
            <li key={s.name} className="flex items-center justify-between py-3">
              <span className="text-base md:text-lg text-foreground/85">{s.name}</span>
              <span className="text-base md:text-lg font-medium">{s.price}</span>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-foreground/70 max-w-3xl">
          Se hela vår <Link to="/priser/" className="underline">prislista</Link>.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Öppettider – Barberare Norrmalm</h2>
        <ul className="mt-4 space-y-2 text-base md:text-lg text-foreground/85 max-w-md">
          <li><strong>Måndag–Fredag:</strong> 10:00–20:00</li>
          <li><strong>Lördag:</strong> 10:00–19:00</li>
          <li><strong>Söndag:</strong> 11:00–19:00</li>
        </ul>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Vi har öppet alla dagar – inklusive söndagar – vilket gör oss till ett av få alternativ för
          söndagsklippning i Norrmalm.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Norrmalm – Stockholms mest tillgängliga läge</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Norrmalm är Stockholms centrala affärsdistrikt och ett naturligt nav för pendlare, turister
          och stockholmare som rör sig i city. Att ha en barberare i Norrmalm med kvällsöppet på
          vardagar gör det enkelt att passa in en klippning utan att störa dagen.
        </p>
        <ul className="mt-6 grid gap-3 md:grid-cols-2 max-w-3xl">
          {landmarks.map((l) => (
            <li key={l.name} className="card-elev p-4 flex items-center justify-between">
              <span>{l.name}</span>
              <span className="text-sm text-foreground/70">{l.time}</span>
            </li>
          ))}
        </ul>
      </section>

      <Faq items={faq} heading="Vanliga frågor" />

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Relaterade sidor</h2>
        <ul className="mt-4 flex flex-wrap gap-3">
          <li><Link to="/barberare-stockholm-ahlens-city/" className="btn-outline">Åhléns City</Link></li>
          <li><Link to="/drop-in-barber-stockholm/" className="btn-outline">Drop-in</Link></li>
          <li><Link to="/herrklippning-stockholm/" className="btn-outline">Herrklippning Stockholm</Link></li>
        </ul>
      </section>

      <section className="container-page pb-16">
        <div className="card-elev p-8 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl">Boka tid i Norrmalm</h2>
          <p className="mt-3 text-foreground/80">1 minut från T-Centralen. Öppet alla dagar.</p>
          <div className="mt-6">
            <Button asChild size="lg">
              <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">Boka nu</a>
            </Button>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
